// Location QR API — staff site/block/floor/amenity QRs (/location-qr/*)

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired.');
        return null;
      }
      return idToken.toString();
    }
    return null;
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

const parseApiError = async response => {
  let message = `API Error: ${response.status}`;
  try {
    const data = await response.json();
    message = data.error || data.message || message;
  } catch {
    /* ignore */
  }
  const error = new Error(message);
  error.status = response.status;
  return error;
};

const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();
  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    throw error;
  }

  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  if (body != null) {
    config.body = JSON.stringify(body);
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, config);

  if (!response.ok) {
    const error = await parseApiError(response);
    error.endpoint = endpoint;
    error.method = method;

    loggingService
      .logError(
        'api_error_location_qr',
        `Location QR API error: ${response.status} for ${endpoint}`,
        error,
        { endpoint, method, status: response.status }
      )
      .catch(() => {});

    throw error;
  }

  return response;
};

const readJson = async response => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

export const locationQrApi = {
  /**
   * GET /location-qr
   * @param {Object} [params]
   * @param {string} [params.site_id]
   * @param {string} [params.location_type] - block_floor | amenity
   * @param {string} [params.block_id]
   * @param {string} [params.amenity_id]
   * @param {string} [params.search]
   * @param {number} [params.limit]
   * @param {string} [params.last_key] - JSON string of last_evaluated_key
   */
  list: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.site_id) qs.set('site_id', params.site_id);
    if (params.location_type) qs.set('location_type', params.location_type);
    if (params.block_id) qs.set('block_id', params.block_id);
    if (params.amenity_id) qs.set('amenity_id', params.amenity_id);
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.last_key) qs.set('last_key', params.last_key);

    const query = qs.toString();
    const endpoint = `/location-qr${query ? `?${query}` : ''}`;
    const response = await makeApiRequest(endpoint, 'GET');
    const data = await readJson(response);

    return {
      items: Array.isArray(data.items) ? data.items : [],
      count: data.count ?? 0,
      last_evaluated_key: data.last_evaluated_key ?? null,
      has_more: Boolean(data.has_more),
    };
  },

  listAllItems: async (params = {}) => {
    const items = [];
    let lastKey = null;
    let hasMore = true;

    while (hasMore) {
      const page = await locationQrApi.list({
        ...params,
        limit: params.limit || 100,
        last_key: lastKey,
      });
      items.push(...(page.items || []));
      hasMore = page.has_more;
      lastKey = page.last_evaluated_key
        ? JSON.stringify(page.last_evaluated_key)
        : null;
      if (hasMore && !lastKey) break;
    }

    return items;
  },

  /** GET /location-qr/info?qr_id= */
  getInfo: async qrId => {
    if (!qrId) throw new Error('qr_id is required');
    const response = await makeApiRequest(
      `/location-qr/info?qr_id=${encodeURIComponent(qrId)}`,
      'GET'
    );
    return readJson(response);
  },

  /** POST /location-qr */
  create: async body => {
    const response = await makeApiRequest('/location-qr', 'POST', body);
    return readJson(response);
  },

  /** DELETE /location-qr */
  delete: async qrId => {
    if (!qrId) throw new Error('qr_id is required');
    const response = await makeApiRequest('/location-qr', 'DELETE', { qr_id: qrId });
    return readJson(response);
  },

  /** POST /location-qr/preview-count */
  previewCount: async body => {
    const response = await makeApiRequest('/location-qr/preview-count', 'POST', body);
    return readJson(response);
  },

  /** POST /location-qr/bulk-generate */
  bulkGenerate: async body => {
    const response = await makeApiRequest('/location-qr/bulk-generate', 'POST', body);
    return readJson(response);
  },

  /** POST /location-qr/scan */
  scan: async body => {
    const response = await makeApiRequest('/location-qr/scan', 'POST', body);
    return readJson(response);
  },
};
